<template>
  <div
    data-guided-search-wrapper
    class="absolute inset-0 overflow-x-hidden overflow-y-auto mt-16 bg-white"
  >
    <vetster-confirm class="pl-0 md:pl-32" />
    <vetster-search
      v-if="!loading"
      :country="country"
      :locale="locale"
      :mobile-apps="mobileApps"
      :is-recommended="isRecommended"
      @clear-filters="resetSearch"
    />
  </div>
</template>

<script>
import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faGhost,
  faSlidersH,
} from '@fortawesome/pro-light-svg-icons';
import assign from 'lodash/assign';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import omitBy from 'lodash/omitBy';
import pick from 'lodash/pick';
import { DateTime } from 'luxon';
import { mapActions, mapGetters, mapState } from 'vuex';

import VetsterSearch from '@/brand-components/GuidedSearch/VetsterSearch';
import VetsterConfirm from '@/components/VetsterConfirm';
import useBrowserApiHelpers from '@/composables/use-browser-api-helpers';
import useSearchData from '@/composables/use-search-data';
import useSmartTime from '@/composables/use-smart-time';
import SearchType from '@/enums/SearchType';
import localeDefaultLocation from '@/helpers/localeDefaultLocation';
import { getQueryParams } from '@/helpers/url';
import {
  getLocaleSlug,
  isMarketSiteCountry,
} from '@/i18n';
import LocalizesRoutes from '@/mixins/LocalizesRoutes';
import ManagesClientSession from '@/mixins/ManagesClientSession';
import ManagesLocaleCookies from '@/mixins/ManagesLocaleCookies';
import ManagesSearchCookies from '@/mixins/ManagesSearchCookies';
import TrackSearches from '@/mixins/TrackSearches';
import SearchInputs from '@/models/SearchInputs';

library.add({
  faGhost,
  faSlidersH,
});

export default {
  name: 'VetsterGuidedSearch',

  components: {
    VetsterConfirm,
    VetsterSearch,
  },

  mixins: [
    LocalizesRoutes,
    ManagesClientSession,
    ManagesSearchCookies,
    ManagesLocaleCookies,
    TrackSearches,
  ],

  props: {
    locale: {
      type: String,
      default: null,
    },
    country: {
      type: String,
      default: 'ca',
    },
    globalCurrency: {
      type: String,
      default: '',
    },
    filters: {
      type: [Object, Array],
      default: () => ({}),
    },
    geocode: {
      type: Boolean,
      default: false,
    },
    isGlobalExperience: {
      type: Boolean,
      default: false,
    },
    mobileApps: {
      type: [Object, Array],
      default: () => ({}),
    },
    isRecommended: {
      type: Boolean,
      default: false,
    },
  },

  setup() {
    const { localStorageAvailable } = useBrowserApiHelpers();
    const {
      searchConcernLimit,
      validSearchData,
    } = useSearchData();
    const {
      searchTimeTo,
      searchTimeFrom,
      validDateFrom,
      validDateTimeSearchData,
    } = useSmartTime();

    return {
      localStorageAvailable,
      searchConcernLimit,
      searchTimeTo,
      searchTimeFrom,
      validDateFrom,
      validSearchData,
      validDateTimeSearchData,
    };
  },

  data() {
    return {
      pageReady: false,
      loading: true,
      timezone: DateTime.local().zoneName,
    };
  },

  computed: {
    ...mapGetters(['getSiteCurrency']),
    ...mapGetters('geolocation', ['getGeolocation']),
    ...mapState('search', [
      'dates',
      'inputs',
      'page',
    ]),
  },

  watch: {
    async isClient() {
      await this.$store.dispatch('subscriptions/fetchSubscriptions');
    },
  },

  async created() {
    await this.checkAuthAndUser();
    const localParams = this.getLocalStorageSearchParams();
    await this.$store.commit('setIsGlobalUser', this.isGlobalExperience);
    await this.fetchPets();
    await this.$store.dispatch('petConcerns/fetchPetConcerns');

    if (this.isGlobalUser && this.globalCurrency) {
      await this.$store.dispatch('search/fetchCurrencyConversion', this.globalCurrency);
    }

    let searchData = await this.setInputsFromLocalStorage();
    const routeSearchParams = omitBy(this.paramsFromRouteParams(), isNil);

    // GSV2 date_from should be null if not set in local storage
    if (!localParams.date_from) {
      routeSearchParams.date_from = null;
    }

    if (get(routeSearchParams, 'search_uuid')) {
      this.setSearchResult(routeSearchParams);

      this.pageReady = true;
      this.loading = false;

      return;
    }

    searchData = { ...searchData, ...routeSearchParams };

    const validCurrency = { currency: this.getSiteCurrency }; // This is until we support multiple currencies on the same market site

    searchData = Object.assign(searchData, validCurrency);

    searchData = await this.resetLocationOnCountryMismatch(searchData);

    const geoCode = await this.fetchGeoWhenMissing(searchData);

    if (geoCode) {
      searchData = assign(searchData, geoCode);
      searchData = await this.updateLocationOnCountryMismatch(searchData);
    }

    this.$nextTick(async () => {
      await this.$store.commit('search/setInputs', searchData);
    });
    await this.setDatesFromInput();
    this.pageReady = true;
    this.loading = false;
  },

  mounted() {
    const routeSearchParams = omitBy(this.paramsFromRouteParams(), isNil);

    this.trackSearchLoad(get(routeSearchParams, 'type'), '2.0');
  },

  methods: {
    ...mapActions(['checkAuthAndUser']),
    ...mapActions('geolocation', ['fetchGeolocation']),
    getLocalStorageSearchParams() {
      if (this.localStorageAvailable()) {
        const { searchParams } = localStorage;

        if (typeof searchParams !== 'undefined') {
          return JSON.parse(searchParams);
        }
      }

      return {};
    },
    resetSearch() {
      this.resetForm();

      window.history.pushState(
        null,
        this.$t('search.vetster-search-results'),
        this.guidedSearchUrl,
      );
    },
    async setDatesFromInput() {
      await this.$store.commit('search/setDates', {
        date_from: this.inputs.date_from,
        date_to: this.inputs.date_to,
        timezone: this.inputs.timezone,
      });
    },
    setSearchResult(params) {
      this.$store.commit('search/setInputs', {
        currency: get(params, 'currency', this.getSiteCurrency),
        timezone: get(params, 'timezone', this.timezone),
        ...pick(params, [
          'city',
          'country',
          'province',
          'search_result_uuid',
          'search_uuid',
        ]),
      });
    },
    paramsFromRouteParams() {
      const routeParams = getQueryParams();

      if (isEmpty(routeParams)) {
        return {};
      }

      const searchInputs = new SearchInputs();
      const validDateTimeParams = this.validDateTimeSearchData(routeParams);
      const concerns = this.filterValidConcerns(
        get(routeParams, 'concerns', get(routeParams, 'concern', '')).split(','),
      ).slice(0, this.searchConcernLimit);
      const pet = this.findPet(get(routeParams, 'pet_uuid'));
      const speciesTreated = this.filterValidSpecies([get(routeParams, 'species_treated', '')]);

      this.setConcernsCookie(concerns);

      if (pet) {
        this.setPetCookie(pet);
      }

      this.setSpeciesTreatedCookie(speciesTreated);

      return {
        date_from: get(validDateTimeParams, 'date_from', ''),
        date_to: '',
        time_from: get(routeParams, 'time_from', 800),
        time_to: get(routeParams, 'time_to', 2000),
        timezone: get(routeParams, 'timezone', this.timezone),
        city: get(routeParams, 'city', ''),
        province: get(routeParams, 'province', ''),
        currency: get(routeParams, 'currency', this.getSiteCurrency),
        country: get(routeParams, 'country', this.country),
        language: get(routeParams, 'language', this.language) || 'en',
        full_name: get(routeParams, 'full_name', searchInputs.full_name),
        areas_of_interest: [],
        species_treated: speciesTreated,
        platform: get(routeParams, 'platform', searchInputs.platform),
        pet_uuid: pet?.uuid,
        concerns,
        search_uuid: get(routeParams, 'search_uuid', null),
        search_result_uuid: get(routeParams, 'search_result_uuid', null),
        type: SearchType.values().includes(get(routeParams, 'type')) ? get(routeParams, 'type') : SearchType.GuidedSearch,
      };
    },
    paramsFromLocalStorage() {
      const localParams = this.getLocalStorageSearchParams();

      const searchInputs = new SearchInputs();
      const validDateTimeParams = this.validDateTimeSearchData(localParams);
      const concerns = get(localParams, 'concerns', get(localParams, 'concern', []));
      const pet = this.findPet(get(localParams, 'pet_uuid'));

      // TODO Delete stale permanent browser cache, this is now named 'concerns'.
      if ('concern' in localParams) {
        this.$store.commit('search/localStorageDelete', 'concern');
      }

      // TODO Delete stale permanent browser cache, ?pet_uuid= & v-search-pet are now sanitized.
      if (!pet && 'pet_uuid' in localParams) {
        this.$store.commit('search/localStorageDelete', 'pet_uuid');
      }

      return {
        date_from: get(validDateTimeParams, 'date_from', ''),
        date_to: '',
        time_from: get(localParams, 'time_from', 800),
        time_to: get(localParams, 'time_to', 2000),
        timezone: get(localParams, 'timezone', this.timezone),
        city: get(localParams, 'city', ''),
        province: get(localParams, 'province', ''),
        currency: get(localParams, 'currency', this.getSiteCurrency),
        country: get(localParams, 'country', this.country),
        language: get(localParams, 'language', this.language) || 'en',
        full_name: get(localParams, 'full_name', searchInputs.full_name),
        areas_of_interest: [],
        species_treated: get(localParams, 'species_treated', []),
        platform: get(localParams, 'platform', searchInputs.platform),
        pet_uuid: pet?.uuid,
        concerns,
      };
    },
    async resetLocationOnCountryMismatch(searchData) {
      if (this.country.toLowerCase() === searchData?.country?.toLowerCase()) {
        return searchData;
      }

      return assign(searchData, {
        country: null,
        city: null,
        province: null,
      });
    },
    async updateLocationOnCountryMismatch(searchData) {
      const searchCountry = searchData?.country?.toString()?.toLowerCase();

      if ((!this.isGlobalUser && (!searchData.country || this.country === searchData.country))
        || (this.isGlobalUser && searchCountry && !isMarketSiteCountry(searchCountry))
      ) {
        return searchData;
      }

      // TODO: Remove this line?
      // sessionStorage.removeItem('blockLocaleRedirect');

      if (isMarketSiteCountry(searchCountry)
        && !sessionStorage.getItem('blockLocaleRedirect')
      ) {
        if (
          await this.$confirm(
            this.$t('search.it-looks-like-you-may-be-located-in', {
              location: this.$t(`country-code.${searchCountry}`),
            }),
            this.$t('app.search-by-location'),
            {
              confirmText: this.$t('search.change-to-country', { country: this.$t(`country-code.${searchCountry}`) }),
              cancelText: this.$t('search.search-here-instead'),
              icon: ['fal', 'map-marker'],
            },
          )
        ) {
          this.clearSession();
          this.setLocaleCookies(getLocaleSlug(searchCountry), false);

          this.redirectToMarketSite(searchCountry);
        } else {
          this.storeUserRedirectionKey();
        }
      }

      return assign(searchData, {
        country: null,
        city: null,
        province: null,
      });
    },
    storeUserRedirectionKey() {
      sessionStorage.setItem('blockLocaleRedirect', '1');
    },
    redirectToMarketSite(searchCountry) {
      window.location.href = `/${getLocaleSlug(searchCountry)}/gs`;
    },
    async fetchGeoWhenMissing(searchData) {
      if (!this.geocode || (searchData.province && searchData.city)) {
        return null;
      }

      if (localStorage.skipNextGeocode) {
        localStorage.removeItem('skipNextGeocode');

        return null;
      }

      this.loading = true;

      await this.fetchGeolocation();

      try {
        if (this.getGeolocation.country) {
          return {
            city: this.getGeolocation.city,
            province: this.getGeolocation.province,
            country: this.getGeolocation.country.toLowerCase(),
          };
        }

        return localeDefaultLocation(this.isGlobalUser);
      } finally {
        // this.loading = false;
      }
    },
    async setInputsFromLocalStorage() {
      const searchParams = this.paramsFromLocalStorage();
      const validDateTimeSearchData = this.validDateTimeSearchData(searchParams);
      const validSearchData = this.validSearchData(searchParams);
      const searchData = {};
      Object.assign(searchData, validSearchData, validDateTimeSearchData);

      return searchData;
    },
    async resetForm() {
      this.forgetSearchCookies();
      await this.$store.commit('search/setInputs', {
        pet_uuid: null,
        species_treated: [],
        areas_of_interest: [],
        concerns: [],
        language: 'en',
        search_uuid: null,
        search_result_uuid: null,
        uuid: null,
        full_name: null,
      });
    },
  },
};
</script>
